import { Link } from "react-router-dom";
import PublicSiteHeader from "@/components/PublicSiteHeader";
import MarketplaceCategoryNav from "@/components/MarketplaceCategoryNav";
import MarketplaceBreadcrumb from "@/components/MarketplaceBreadcrumb";
import { ArrowRight, Activity, Droplet, Syringe, Heart, ShieldCheck, Phone, Mail, Award, Bike, Stethoscope, Armchair, Wind, Bandage, Dumbbell, Bath, Footprints } from "lucide-react";

// Medical Devices — Sterile Mate's legacy product offering landing page.
// Serves as a small business for the classic Sterile Mate branded product lines:
// Blood Pressure Monitors, Blood Collection, Needles & Syringes, Patient Care.

const SUBCATEGORIES = [
  {
    slug: "blood-pressure-monitors",
    name: "Blood Pressure Monitors",
    brief: "BPBIO series · Manual, automated, and self-testing kiosks",
    count: 3,
    icon: Activity,
    tone: "from-rose-500 to-orange-500",
    accent: "text-rose-400",
    ring: "hover:ring-rose-400/40",
    href: "/marketplace?category=blood-pressure-monitors",
    products: [
      "BPBIO 220 — Mercury-Free Manual",
      "BPBIO 250 — Fully Automated Clinical",
      "BPBIO 320S — Self-Testing Kiosk",
    ],
    image: "https://sterilemate.com/wp-content/uploads/2023/05/BPBIO250_front_side_with-cuff_TP-2-1.jpg",
  },
  {
    slug: "vive-power-mobility",
    name: "Power Mobility",
    brief: "Electric scooters, power wheelchairs, mobility batteries",
    count: 5,
    icon: Bike,
    tone: "from-sky-500 to-blue-600",
    accent: "text-sky-400",
    ring: "hover:ring-sky-400/40",
    href: "/marketplace/medical-devices/vive-power-mobility",
    products: ["4-Wheel Mobility Scooter", "3-Wheel Mobility Scooter", "Compact Folding Power Wheelchair"],
    image: "https://www.vivehealth.com/cdn/shop/files/2a_4_Wheel_Mobility_Scooter_6_512x512.jpg",
  },
  {
    slug: "vive-mobility",
    name: "Mobility",
    brief: "Rollators, walkers, canes, transport chairs, reachers",
    count: 8,
    icon: Bike,
    tone: "from-blue-500 to-indigo-600",
    accent: "text-blue-400",
    ring: "hover:ring-blue-400/40",
    href: "/marketplace/medical-devices/vive-mobility",
    products: ["Upright Rollator", "Sunstride Rollator", "Folding Cane", "Quad Cane"],
    image: "https://www.vivehealth.com/cdn/shop/files/red-mobility_1350x651.png",
  },
  {
    slug: "vive-bracing",
    name: "Bracing",
    brief: "Knee, back, ankle, wrist, shoulder braces & supports",
    count: 6,
    icon: Stethoscope,
    tone: "from-teal-500 to-cyan-600",
    accent: "text-teal-400",
    ring: "hover:ring-teal-400/40",
    href: "/marketplace/medical-devices/vive-bracing",
    products: ["Hinged Knee Brace", "Lower Back Support", "Ankle Brace", "Wrist Brace"],
    image: "https://www.vivehealth.com/cdn/shop/files/2b_Rotator_Cuff_Cold_Therapy_1_512x512.jpg",
  },
  {
    slug: "vive-health-devices",
    name: "Health Devices",
    brief: "TENS units, pulse oximeters, thermometers, scales",
    count: 7,
    icon: Stethoscope,
    tone: "from-emerald-500 to-teal-600",
    accent: "text-emerald-400",
    ring: "hover:ring-emerald-400/40",
    href: "/marketplace/medical-devices/vive-health-devices",
    products: ["8-Mode TENS Unit", "Fingertip Pulse Oximeter", "Digital Bathroom Scale"],
    image: "https://www.vivehealth.com/cdn/shop/files/Overbed_Table_12_1_893x430.png",
  },
  {
    slug: "vive-bathroom",
    name: "Bathroom",
    brief: "Grab bars, shower chairs, toilet rails, transfer benches",
    count: 7,
    icon: Bath,
    tone: "from-cyan-500 to-blue-600",
    accent: "text-cyan-400",
    ring: "hover:ring-cyan-400/40",
    href: "/marketplace/medical-devices/vive-bathroom",
    products: ["Rise-Up Toilet Rail", "Metal Grab Bar", "Transfer Bench"],
    image: "https://www.vivehealth.com/cdn/shop/files/Banner_3_2000x834.jpg",
  },
  {
    slug: "vive-bedroom",
    name: "Bedroom",
    brief: "Bed rails, wedges, overbed tables, transfer aids",
    count: 6,
    icon: Armchair,
    tone: "from-amber-500 to-orange-600",
    accent: "text-amber-400",
    ring: "hover:ring-amber-400/40",
    href: "/marketplace/medical-devices/vive-bedroom",
    products: ["Bed Wedge Pillow", "Overbed Table", "Bed Assist Rail"],
    image: "https://www.vivehealth.com/cdn/shop/files/Overbed_Table_12_1_893x430.png",
  },
  {
    slug: "vive-living-aids",
    name: "Living Aids",
    brief: "Reachers, dressing sticks, sock aids, adaptive utensils",
    count: 6,
    icon: Heart,
    tone: "from-violet-500 to-purple-600",
    accent: "text-violet-400",
    ring: "hover:ring-violet-400/40",
    href: "/marketplace/medical-devices/vive-living-aids",
    products: ["Hip & Knee Recovery Kit", "Sock Aid", "23\" Shoe Horn"],
    image: "https://www.vivehealth.com/cdn/shop/files/2_Rotating_Reacher_2_512x512.jpg",
  },
  {
    slug: "vive-footcare",
    name: "Footcare",
    brief: "Plantar fasciitis relief, arch supports, toe separators",
    count: 7,
    icon: Footprints,
    tone: "from-fuchsia-500 to-pink-600",
    accent: "text-fuchsia-400",
    ring: "hover:ring-fuchsia-400/40",
    href: "/marketplace/medical-devices/vive-footcare",
    products: ["Plantar Fasciitis Bundle", "Toe Separators", "Arch Support Insoles"],
    image: "https://www.vivehealth.com/cdn/shop/products/PlantarFasciitisBundle_75d6db76-558b-448a-80ce-b76808500139_512x512.png",
  },
  {
    slug: "vive-physical-therapy",
    name: "Physical Therapy",
    brief: "TENS, foam rollers, resistance bands, hot/cold therapy",
    count: 8,
    icon: Dumbbell,
    tone: "from-green-500 to-emerald-600",
    accent: "text-green-400",
    ring: "hover:ring-green-400/40",
    href: "/marketplace/medical-devices/vive-physical-therapy",
    products: ["Foam Roller Bundle", "Resistance Bands", "8-Mode TENS Unit"],
    image: "https://www.vivehealth.com/cdn/shop/products/HomeWorkoutBundle_512x512.png",
  },
  {
    slug: "patient-care",
    name: "Patient Care",
    brief: "Bedside essentials, comfort items, mobility & daily-living aids",
    count: 0,
    icon: Heart,
    tone: "from-violet-500 to-fuchsia-500",
    accent: "text-violet-400",
    ring: "hover:ring-violet-400/40",
    href: "#",
    products: ["Adding soon — reach out for allocation reservations"],
    image: null,
    comingSoon: true,
  },
];

const CREDS = [
  { label: "FDA-Registered Manufacturers", icon: ShieldCheck },
  { label: "certified small business Certified Distribution", icon: Award },
  { label: "OSHA-Compliant Safety Engineering", icon: ShieldCheck },
  { label: "Same-Day Quote · 5-Day Standard Lead", icon: Award },
];

export default function MedicalDevicesLandingPage() {
  return (
    <div className="min-h-screen bg-[#0a0f1a]" data-testid="medical-devices-page">
      <PublicSiteHeader />
      <MarketplaceCategoryNav currentCategory="medical-devices" />
      <MarketplaceBreadcrumb trail={[{ label: "Medical Devices" }]} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#0a1628] via-[#0b1d3a] to-[#0a0f1a] border-b border-slate-800">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #0056A8 1px, transparent 0)", backgroundSize: "28px 28px" }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 relative">
          <div className="max-w-3xl">
            <p className="text-[11px] font-black uppercase tracking-[3px] text-cyan-400 mb-4" data-testid="md-eyebrow">
              Sterile Mate · Legacy Product Line
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] tracking-tight mb-6" data-testid="md-headline">
              Medical Devices.<br />
              <span className="text-cyan-400">The clinical portfolio Sterile Mate was built on.</span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              Before we became a full-service healthcare distribution &amp; RFQ platform, Sterile Mate started as a supplier of clinically-validated blood pressure monitors, blood collection systems, and safety-engineered needles &amp; syringes. This is the legacy portfolio &mdash; still active, still stocked, still trusted by clinics, pharmacies, and health systems across the U.S.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/hospital/marketplace"
                data-testid="md-hero-cta-browse"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-black text-sm transition-all"
              >
                Browse the full marketplace <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="mailto:orders@sterilemate.com?subject=Medical%20Devices%20quote"
                data-testid="md-hero-cta-quote"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-slate-600 hover:border-cyan-400 text-white text-sm font-bold transition-all"
              >
                <Mail className="w-4 h-4" /> Request a quote
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SUBCATEGORY GRID */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[2.5px] text-cyan-500 mb-2">Product Categories</p>
            <h2 className="text-3xl lg:text-4xl font-black text-white leading-tight tracking-tight">
              Four legacy product lines.<br />
              <span className="text-slate-500">All stockable. All on the same purchase order.</span>
            </h2>
          </div>
          <p className="text-slate-400 max-w-md text-sm leading-relaxed">
            Any of these can be added to your regular EVS or surgical PO. Cross-category discounts apply on orders over $2,500.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {SUBCATEGORIES.map((c) => {
            const Icon = c.icon;
            const Wrap = c.comingSoon ? "div" : Link;
            const wrapProps = c.comingSoon ? {} : { to: c.href };
            return (
              <Wrap
                key={c.slug}
                {...wrapProps}
                data-testid={`md-subcat-${c.slug}`}
                className={`group relative overflow-hidden rounded-2xl bg-[#0b1d3a] border border-slate-800 hover:border-slate-700 ring-1 ring-transparent ${c.ring} transition-all ${c.comingSoon ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <div className="grid grid-cols-5">
                  <div className="col-span-3 p-7 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${c.tone} flex items-center justify-center shadow-lg`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      {c.comingSoon ? (
                        <span className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full bg-slate-800 text-slate-400">Coming soon</span>
                      ) : (
                        <span className={`text-[10px] font-black uppercase tracking-widest ${c.accent}`}>{c.count} products</span>
                      )}
                    </div>
                    <div>
                      <h3 className="text-2xl font-black text-white leading-tight mb-1.5 tracking-tight">{c.name}</h3>
                      <p className="text-sm text-slate-400 leading-relaxed">{c.brief}</p>
                    </div>
                    <ul className="space-y-1.5 pt-2">
                      {c.products.map((p) => (
                        <li key={p} className="flex items-start gap-2 text-xs text-slate-500">
                          <span className={`inline-block w-1 h-1 rounded-full ${c.accent.replace("text-", "bg-")} mt-1.5 shrink-0`} />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                    {!c.comingSoon && (
                      <div className={`inline-flex items-center gap-1.5 text-xs font-bold ${c.accent} group-hover:gap-2.5 transition-all pt-2`}>
                        View products <ArrowRight className="w-3.5 h-3.5" />
                      </div>
                    )}
                  </div>
                  <div className="col-span-2 relative bg-slate-900/40 flex items-center justify-center border-l border-slate-800/60 overflow-hidden">
                    {c.image ? (
                      <img
                        src={c.image}
                        alt={c.name}
                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    ) : (
                      <Icon className="w-20 h-20 text-slate-700" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-[#0b1d3a]/60" />
                  </div>
                </div>
              </Wrap>
            );
          })}
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="bg-[#060a12] border-y border-slate-800/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
          <p className="text-center text-[11px] font-black uppercase tracking-[2.5px] text-slate-500 mb-6">Built on 5+ years of Texas Medical Center trust</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {CREDS.map((c) => {
              const I = c.icon;
              return (
                <div key={c.label} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center shrink-0">
                    <I className="w-4 h-4 text-cyan-400" />
                  </div>
                  <p className="text-xs text-slate-400 leading-snug font-semibold">{c.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="rounded-2xl bg-gradient-to-br from-[#0f3460] via-[#0056A8] to-[#003366] p-8 lg:p-12 border border-cyan-500/20 relative overflow-hidden">
          <div className="absolute right-0 top-0 w-64 h-64 bg-cyan-400/10 rounded-full blur-3xl -translate-y-16 translate-x-16" />
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[3px] text-cyan-300 mb-3">Ready to onboard?</p>
              <h3 className="text-3xl font-black text-white leading-tight tracking-tight mb-4">
                Get pricing on the whole medical-devices portfolio in one email.
              </h3>
              <p className="text-slate-200 text-[15px] leading-relaxed">
                Tell us your usage &mdash; boxes per month, per SKU, or just the category &mdash; and we&apos;ll price out the full portfolio with 2026 net + retail on the same PO.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <a
                href="mailto:orders@sterilemate.com?subject=Medical%20Devices%20portfolio%20quote"
                data-testid="md-cta-email"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-white hover:bg-slate-100 text-slate-900 font-black text-sm transition-all"
              >
                <Mail className="w-4 h-4" /> orders@sterilemate.com
              </a>
              <a
                href="tel:8009883892"
                data-testid="md-cta-phone"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl border border-white/30 hover:bg-white/10 text-white font-black text-sm transition-all"
              >
                <Phone className="w-4 h-4" /> 800.988.3892
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
